import { useCallback } from 'react';
import { useSnackbar } from 'notistack';

export const useSourceDataLoader = (
  nodeId,
  edges,
  isInitialized,
  connection,
  profileSourceData,
  addLog,
  setFetchedHeader,
  setHeaders,
  setFormField,
  setHeaderName,
  setTemplateData,
  setDisableForm,
  setJoins,
  setMappings,
  setTargetSchema,
  setSources,
  dataModel,
  dataModelformField,
  setDataModelformField,
  attributes
) => {
  const { enqueueSnackbar } = useSnackbar();

  const getLocalData = useCallback(async () => {
    console.log('Starting getLocalData...');

    try {
      // ✅ DATA MODEL FLOW
      if (dataModel) {
        const sourceNode = dataModelformField?.source || {};
        const duckMeta = sourceNode?.sampleMetaData || {};
        const columnMeta = duckMeta?.columns || [];
        const sampleRows = duckMeta?.sampleRows || [];

        const tableName =
          sourceNode?.formField?.alias ||
          sourceNode?.formField?.path?.split('/').pop()?.split('.')[0] ||
          sourceNode?.nodeName ||
          sourceNode?.step_name ||
          'source_table';

        const oneSource = [
          {
            id: 'source_1',
            name: tableName,
            nodeId: sourceNode?.nodeId || 'source_node',
            stepName: sourceNode?.step_name || sourceNode?.nodeName || 'Source',
            expanded: true,
            columns: columnMeta
              .map((col) => col.name || col.column_name)
              .filter(Boolean)
              .sort(),

            dataTypes: columnMeta.reduce((acc, col) => {
              const columnName = col.name || col.column_name;
              const columnType = col.type || col.data_type;

              if (columnName) {
                acc[columnName] = columnType;
              }

              return acc;
            }, {}),
            sampleRows,
          },
        ];

        console.log({ attributes })
        const targetSchemas = [
          {
            id: 'target_1',
            nodeId: 'target_node',
            tableName: dataModelformField?.entityName || 'target_table',
            columns: (attributes || []).map((attr) => ({
              column_name: attr.name,
              data_type: attr.data_type,
            })),
            sampleRows: [],
          },
        ];

        setSources(oneSource);
        setTargetSchema(targetSchemas);

        addLog(`✅ Loaded 1 source from data model`);
        addLog(`✅ Loaded ${attributes?.length || 0} target attributes`);

        return;
      }

      // ✅ EXISTING NORMAL FLOW
      const store = JSON.parse(sessionStorage.getItem('allNodes'));
      console.log('store : ', store);

      const findSrcNodeId = [];
      const findTargetNodeId = [];

      edges.forEach((el) => {
        if (el.target === nodeId) {
          findSrcNodeId.push(el.source);
        }

        if (el.source === nodeId) {
          findTargetNodeId.push(el.target);
        }
      });

      if (!store) return;

      const allReadHeaders = findSrcNodeId.flatMap((item) => {
        const node = store.find((n) => n.nodeId === item);
        if (!node || !node.step_name || !node?.sampleMetaData) return [];

        const duckMeta = node.sampleMetaData || {};
        const columnMeta = duckMeta.columns || [];
        const sampleRows = duckMeta.sampleRows || [];

        const tableName =
          node.formField?.alias ||
          node.formField?.path?.split('/').pop()?.split('.')[0] ||
          `source_${node.step_name || 'table'}`;

        return columnMeta.map((col) => ({
          columnName: col.name || col.column_name,
          dataType: col.type || col.data_type,
          sourceTable: tableName,
          sourceNodeId: node.nodeId,
          sourceStepName: node.step_name,
          sampleRows,
        }));
      });

      const destinationInfo = null;

      const targetSchemas = findTargetNodeId.flatMap((targetId) => {
        const node = store.find((n) => n.nodeId === targetId);
        if (!node) return [];
        if (!(node.step_name?.includes('Write') || node.step_name === 'FetchSchema')) {
          return [];
        }

        console.log('NODE 2: ', node);

        const columnMeta = node.columns || [];
        const sampleRows = node.sampleRows || [];

        return [
          {
            id: `target_${targetId}`,
            nodeId: node.nodeId,
            tableName: node.params?.tablename || node.formField?.tablename || 'target_table',
            columns: columnMeta.map((col) => ({
              column_name: col.column_name ?? col.name ?? col.columnName,
              data_type: col.data_type ?? col.dataType ?? col.type,
            })),
            sampleRows,
          },
        ];
      });

      setTargetSchema(targetSchemas);

      const sourceMap = new Map();

      allReadHeaders.forEach((header) => {
        const sourceKey = `${header.sourceTable}_${header.sourceNodeId}`;

        if (!sourceMap.has(sourceKey)) {
          sourceMap.set(sourceKey, {
            id: `source_${sourceMap.size + 1}`,
            name: header.sourceTable,
            nodeId: header.sourceNodeId,
            stepName: header.sourceStepName,
            expanded: sourceMap.size === 0,
            columns: [],
            dataTypes: {},
            sampleRows: header.sampleRows || [],
          });
        }

        const source = sourceMap.get(sourceKey);

        if (!source.columns.includes(header.columnName)) {
          source.columns.push(header.columnName);
          source.dataTypes[header.columnName] = header.dataType;
        }
      });

      const sourceArray = Array.from(sourceMap.values()).map((source) => ({
        ...source,
        columns: source.columns.sort(),
      }));

      setSources(sourceArray);

      addLog(`✅ Loaded ${sourceArray.length} sources`);
      if (destinationInfo) {
        addLog(`✅ Loaded destination: ${destinationInfo.destination_name}`);
      }
    } catch (error) {
      console.error('Error loading source data:', error);
      enqueueSnackbar('Error loading datasets', { variant: 'error' });
      addLog(`❌ Error loading sources: ${error.message}`);
    }
  }, [nodeId, edges, addLog, setSources, setTargetSchema, enqueueSnackbar, dataModel, dataModelformField, attributes]);

  return { getLocalData };
};
